<template>
    <div class="results">
        <div>
            <div class="flex-cols flex-cols-1 md:flex-cols-2">
                <div>
                    <h2 class="h4">Reflection & Analysis</h2>
                    <p>Which category are you seeking the biggest change? In general, this would be your primary area of focus
                        with which to set a big goal. When considering this area, what emotions come to mind? What is the driving
                        force behind your desire to see improvement here? How do your values align within this area of your life?
                        Are you trying to keep up with the Joneses? Is there ego behind your desire? Or is the change you are
                        seeking truly authentic? What we are really going after here is your "Why", of which, is a powerful ally in
                        seeing this change come to fruition.
                    </p>
                </div>
                <div>
                    <h2 class="h4">The Power Behind Your “Why”</h2>
                    <p>When the going gets tough, and it will… it's your “Why” that will keep you moving forward. When pursuing
                        big goals, the #1 reason people quit is because they don’t have a strong “Why”. You may authentically want
                        to change, but without understanding why it's important to you, then you have no ammunition to combat the
                        internal voice that will tell you to quit when adversity arrives. The stronger your “Why”, the greater your
                        ability to overcome adversity!</p>
                </div>
            </div>

            <div class="legend grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5">
                <div v-for="(category, index) in categories" class="category">
                    <span class="color" :style="'background-color: ' + universalBackgroundColors[index] + ';'"></span>
                    <span class="label">{{ category.label }}</span>
                </div>
            </div>

            <div class="wheels flex-cols flex-cols-1 md:flex-cols-2">

                <div class="canvas-wrapper">
                    <h3 class="canvas-title h4">Present</h3>
                    <canvas class="present-wheel" ref="presentWheel"></canvas>
                </div>
                <div class="canvas-wrapper">
                    <h3 class="canvas-title h4">Future</h3>
                    <canvas class="future-wheel" ref="futureWheel"></canvas>
                </div>
                <div class="canvas-wrapper">
                    <h3 class="canvas-title h4">Present/Future</h3>
                    <div class="stacked-charts">
                        <canvas class="present-wheel" ref="presentWheelStacked"></canvas>
                        <canvas class="future-wheel" ref="futureWheelStacked"></canvas>
                    </div>
                </div>
                <div class="canvas-wrapper">
                    <h3 class="canvas-title h4">Gap</h3>
                    <canvas class="gap-analysis-wheel" ref="gapAnalysisWheel"></canvas>
                </div>
            </div>
            <div class="reflection">

                <h2 class="h4">Why only one area of focus?</h2>
                <p>If you focus on everything, then you focus on nothing! This doesn’t mean that you can’t seek to improve the
                    other areas of your life. However, by having a singular big goal (broken down into smaller goals with
                    identified habits to adopt/modify), you create a positive “gravitational” force that pulls the other areas
                    of your life along with it. </p>
                <h3 class="h5">For example:</h3>
                <p>Your area of focus is health and fitness. You set a big goal to
                    lose 20lbs of excess body fat. In order to accomplish this goal, you begin exercising 3 days per week, then
                    5 days per week, and eventually 6 days per week. With exercise being so important in your schedule, you
                    realize that you need to schedule the other things that are important to you. All of a sudden you are more
                    organized and you are prioritizing what matters most. While at the gym, you are meeting new people and
                    forming new relationships and connections. At work, you are no longer chronically stressed thanks to your
                    regular fitness routine. As such your performance improves and opportunities you dismissed previously are
                    all of a sudden seemingly within reach… and the story continues. In summary, having a primary focus is not
                    limiting, it is empowering!</p>
                <h2 class="h4">Additional Areas for Reflection</h2>
                <p>If there is an area that you scored very low in? Any areas you are not looking to make any significant
                    change? Or any at all? Why is that this area of life is of no concern for you? Are you avoiding something?
                    Is there any fear about making change in this category?
                </p>
                <h2 class="h4">What if no single category stands out?</h2>
                <p>In the event you have multiple categories with the same gap, you will need to reflect further to determine
                    which area of your life would create the greatest impact. Are there any common themes between these areas in
                    which a single goal would benefit them all? If so, you may have stumbled onto something BIG! If not, this
                    might be grounds for the “Miracle Question”.</p>
                <h3 class="h5">The “Miracle Question”</h3>
                <p>Suppose that tonight, while you’re sleeping, a miracle happens! When you wake up
                    tomorrow morning, how will you know that things have suddenly improved? Ask yourself the “Miracle Question”
                    and consider it separately for each area of your life that is tied for the area you desire the most change
                    within. Visualize what your life would look like in context with that category. Do you see other areas of
                    your life also improved within this visualization? What emotions come up? Do you feel stronger about any
                    single area emotionally? In the end, whatever your decision, it should feel right… and hopefully you
                    understand “Why”.</p>

            </div>
        </div>

    </div>

    <div class="next-step">
        <pdfGenerator fileName="wheel-of-life-illustrations.pdf" buttonText="Download Wheel Illustrations" targetElId="pdf-wheels" />
        <pdfGenerator fileName="wheel-of-life-reflection-prompts.pdf" buttonText="Download Reflection Prompts" targetElId="pdf-reflection-prompts" />
    </div>

    <div class="pdf-content-wrapper">
        <div id="pdf-wheels">
            <h2 class="h4" style="text-align: center; margin-bottom: 20px; padding: 0;">The Wheel of Life</h2>
            <div class="wheels flex-cols flex-cols-2">
                <div class="canvas-wrapper">
                    <h3 class="canvas-title h5">Present</h3>
                    <canvas class="present-wheel" ref="presentWheelPdf"></canvas>
                </div>
                <div class="canvas-wrapper">
                    <h3 class="canvas-title h5">Future</h3>
                    <canvas class="future-wheel" ref="futureWheelPdf"></canvas>
                </div>
                <div class="canvas-wrapper">
                    <h3 class="canvas-title h5">Gap</h3>
                    <canvas class="gap-analysis-wheel" ref="gapAnalysisWheelPdf"></canvas>
                </div>
            </div>
            <hr style="margin: 20px 0 0;">
            <p style="text-align: center;">&copy; Greg Harris Coaching. www.gregharriscoaching.com</p>
            <hr style="margin: 0">
            <p>&nbsp;</p>
        </div>

        <div id="pdf-reflection-prompts">

            <div>
                <p style='font-weight: 800; margin-bottom: 0px; padding: 0;'>Reflection & Analysis</p>
                <p>Which category are you seeking the biggest change? In general, this would be your primary area of focus
                    with which to set a big goal. When considering this area, what emotions come to mind? What is the driving
                    force behind your desire to see improvement here? How do your values align within this area of your life?
                    Are you trying to keep up with the Joneses? Is there ego behind your desire? Or is the change you are
                    seeking truly authentic? What we are really going after here is your "Why", of which, is a powerful ally in
                    seeing this change come to fruition.
                </p>
            </div>
            <div>
                <p style='font-weight: 800; margin-bottom: 0px; padding: 0;'>The Power Behind Your “Why”</p>
                <p>When the going gets tough, and it will… it's your “Why” that will keep you moving forward. When pursuing
                    big goals, the #1 reason people quit is because they don’t have a strong “Why”. You may authentically want
                    to change, but without understanding why it's important to you, then you have no ammunition to combat the
                    internal voice that will tell you to quit when adversity arrives. The stronger your “Why”, the greater your
                    ability to overcome adversity!</p>
            </div>

            <div>
                <p style='font-weight: 800; margin-bottom: 0px; padding: 0;'>Why only one area of focus?</p>
                <p>If you focus on everything, then you focus on nothing! This doesn’t mean that you can’t seek to improve
                    other areas of your life. However, by having a singular big goal (broken down into smaller goals with
                    identified habits to adopt/modify), you create a positive “gravitational” force that pulls the other areas
                    of your life along with it. </p>
                <p style='font-weight: 800; margin-bottom: 0px; padding: 0;'>For example:</p>
                <p>Your area of focus is health and fitness. You set a big goal to
                    lose 20lbs of excess body fat. In order to accomplish this goal, you begin exercising 3 days per week, then
                    5 days per week, and eventually 6 days per week. With exercise being so important in your schedule, you
                    realize that you need to schedule the other things that are important to you. All of a sudden you are more
                    organized and you are prioritizing what matters most. While at the gym, you are meeting new people and
                    forming new relationships and connections. At work, you are no longer chronically stressed thanks to your
                    regular fitness routine. As such your performance improves and opportunities you dismissed previously are
                    all of a sudden seemingly within reach… and the story continues. In summary, having a primary focus is not
                    limiting, it is empowering!</p>
            </div>
            <div>
                <p style='font-weight: 800; margin-bottom: 0px; padding: 0;'>Additional Areas for Reflection</p>
                <p>If there is an area that you scored very low in? Any areas you are not looking to make any significant
                    change? Or any at all? Why is that this area of life is of no concern for you? Are you avoiding something?
                    Is there any fear about making change in this category?
                </p>
                <p style='font-weight: 800; margin-bottom: 0px; padding: 0;'>What if no single category stands out?</p>
                <p>In the event you have multiple categories with the same gap, you will need to reflect further to determine
                    which area of your life would create the greatest impact. Are there any common themes between these areas in
                    which a single goal would benefit them all? If so, you may have stumbled onto something BIG! If not, this
                    might be grounds for the “Miracle Question”.</p>
                <p style='font-weight: 800; margin-bottom: 0px; padding: 0;'>The “Miracle Question”</p>
                <p>Suppose that tonight, while you’re sleeping, a miracle happens! When you wake up
                    tomorrow morning, how will you know that things have suddenly improved? Ask yourself the “Miracle Question”
                    and consider it separately for each area of your life that is tied for the area you desire the most change
                    within. Visualize what your life would look like in context with that category. Do you see other areas of
                    your life also improved within this visualization? What emotions come up? Do you feel stronger about any
                    single area emotionally? In the end, whatever your decision, it should feel right… and hopefully you
                    understand “Why”.</p>
            </div>
        <div>
            <hr style="margin: 20px 0 0;">
            <p style="text-align: center;">&copy; Greg Harris Coaching. www.gregharriscoaching.com</p>
            <p style="margin: 0; line-height: 1;">&nbsp;</p>
        </div>

        </div>
    </div>


</template>

<script>
import Chart from 'chart.js/auto';
import pdfGenerator from "./pdfGenerator.vue";

export default {
    components: {pdfGenerator},
    props:      {
        categories:  {
            type: Array
        },
        presentData: {
            type: Array
        },
        futureData:  {
            type: Array
        }
    },
    data() {
        return {
            testDataPresent:           [4, 6, 3, 4, 7, 2, 5, 7, 5],
            testDataFuture:            [6, 7, 7, 5, 8, 5, 8, 8, 7],
            testDataGap:               [2, 1, 4, 1, 1, 3, 3, 1, 2],
            testDataLargestGap:        4,
            largestGap:                0,
            labels:                    [],
            presentBackgroundColors:   [
                'rgba(255, 241, 0, 0.9)',
                'rgba(255, 140, 0, 0.9)',
                'rgba(232, 17, 35, 0.9)',
                'rgba(236, 0, 140, 0.9)',
                'rgba(104, 33, 122, 0.9)',
                'rgba(0, 24, 143, 0.9)',
                'rgba(0, 188, 242, 0.9)',
                'rgba(0, 178, 148, 0.9)',
                'rgba(0, 158, 73, 0.9)',
            ],
            futureBackgroundColors:    [
                'rgba(255, 241, 0, 0.25)',
                'rgba(255, 140, 0, 0.25)',
                'rgba(232, 17, 35, 0.25)',
                'rgba(236, 0, 140, 0.25)',
                'rgba(104, 33, 122, 0.25)',
                'rgba(0, 24, 143, 0.25)',
                'rgba(0, 188, 242, 0.25)',
                'rgba(0, 178, 148, 0.25)',
                'rgba(0, 158, 73, 0.25)',
            ],
            universalBackgroundColors: [
                'rgba(255, 241, 0, 0.67)',
                'rgba(255, 140, 0, 0.67)',
                'rgba(232, 17, 35, 0.67)',
                'rgba(236, 0, 140, 0.67)',
                'rgba(104, 33, 122, 0.67)',
                'rgba(0, 24, 143, 0.67)',
                'rgba(0, 188, 242, 0.67)',
                'rgba(0, 178, 148, 0.67)',
                'rgba(0, 158, 73, 0.67)',
            ],
            optionsSingleCharts:       {
                plugins: {
                    legend: {
                        display: false,
                    },
                },
                scales:  {
                    r: {
                        ticks:       {
                            stepSize: 1, // Set the interval between ticks
                            color:    'black' // Set the color of tick labels
                        },
                        min:         0, // Set the minimum value of the scale
                        max:         10, // Set the maximum value of the scale
                        pointLabels: {
                            display: false,
                        },
                    },
                },
            },
            optionsStackedCharts:      {
                plugins: {
                    legend:  {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            title:  function (tooltipItems) {
                                // console.log(tooltipItems)
                                // return tooltipItems[0].label;
                                return "";
                            },
                            label:  function (tooltipItem) {
                                console.log(tooltipItem);
                                // return 'Present: ' + window.testDataPresent[tooltipItem.dataIndex];
                                return " " + tooltipItem.label;

                            },
                            footer: function (tooltipItems) {
                                return 'Present: ' + window.testDataPresent[tooltipItems[0].dataIndex] + ' Future: ' + window.testDataFuture[tooltipItems[0].dataIndex];
                            }
                        }
                    }
                },
                scales:  {
                    r: {
                        ticks:       {
                            stepSize: 1, // Set the interval between ticks
                            color:    'black' // Set the color of tick labels
                        },
                        min:         0, // Set the minimum value of the scale
                        max:         10, // Set the maximum value of the scale
                        pointLabels: {
                            display: false,
                        },
                    },
                },
            },
        };
    },
    mounted() {
        this.labels = this.categories.map(function (category) {
            return category.label;
        })

        this.createCurrentChart();
        this.createCurrentChartForPdf();
        this.createFutureChart();
        this.createFutureChartForPdf();
        this.createCurrentChartStacked();
        // this.createCurrentChartStackedForPdf();
        this.createFutureChartStacked();
        // this.createFutureChartStackedForPdf();
        this.createGapAnalysisChart();
        this.createGapAnalysisChartForPdf();

        window.testDataPresent = [4, 6, 3, 4, 7, 2, 5, 7, 5];
        window.testDataFuture = [6, 7, 7, 5, 8, 5, 8, 8, 7];
    },
    computed: {
        GapAnalysisData() {
            let gaps = [];
            for (let i = 0; i < this.categories.length; i++) {
                gaps.push(this.futureData[i] - this.presentData[i]);
            }

            this.largestGap = Math.max(...gaps);

            return gaps;
        }
    },
    methods:  {
        createCurrentChart() {
            const ctx = this.$refs.presentWheel.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataPresent,
                            data: this.presentData,
                            // backgroundColor: this.presentBackgroundColors,
                            backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsSingleCharts,
            });
        },
        createCurrentChartForPdf() {
            const ctx = this.$refs.presentWheelPdf.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataPresent,
                            data: this.presentData,
                            // backgroundColor: this.presentBackgroundColors,
                            backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsSingleCharts,
            });
        },
        createFutureChart() {
            const ctx = this.$refs.futureWheel.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataFuture,
                            data: this.futureData,
                            // backgroundColor: this.futureBackgroundColors,
                            backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsSingleCharts,
            });
        },
        createFutureChartForPdf() {
            const ctx = this.$refs.futureWheelPdf.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataFuture,
                            data: this.futureData,
                            // backgroundColor: this.futureBackgroundColors,
                            backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsSingleCharts,
            });
        },
        createCurrentChartStacked() {
            const ctx = this.$refs.presentWheelStacked.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataPresent,
                            data:            this.presentData,
                            backgroundColor: this.presentBackgroundColors,
                            // backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsStackedCharts,
            });
        },
        createCurrentChartStackedForPdf() {
            const ctx = this.$refs.presentWheelStackedPdf.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataPresent,
                            data:            this.presentData,
                            backgroundColor: this.presentBackgroundColors,
                            // backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsStackedCharts,
            });
        },
        createFutureChartStacked() {
            const ctx = this.$refs.futureWheelStacked.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataFuture,
                            data:            this.futureData,
                            backgroundColor: this.futureBackgroundColors,
                            // backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsStackedCharts,
            });
        },
        createFutureChartStackedForPdf() {
            const ctx = this.$refs.futureWheelStackedPdf.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataFuture,
                            data:            this.futureData,
                            backgroundColor: this.futureBackgroundColors,
                            // backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: this.optionsStackedCharts,
            });
        },
        createGapAnalysisChart() {
            const ctx = this.$refs.gapAnalysisWheel.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataGap,
                            data:            this.GapAnalysisData,
                            backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: {
                    plugins: {
                        legend: {
                            display: false,
                        }
                    },
                    scales:  {
                        r: {
                            ticks: {
                                stepSize: 1, // Set the interval between ticks
                                color:    'black' // Set the color of tick labels
                            },
                            min:   0, // Set the minimum value of the scale
                            max:   this.largestGap, // Set the maximum value of the scale
                            // max:         this.testDataLargestGap, // Set the maximum value of the scale
                            pointLabels: {
                                display: false,
                            },
                        },
                    },
                },
            });
        },
        createGapAnalysisChartForPdf() {
            const ctx = this.$refs.gapAnalysisWheelPdf.getContext('2d');
            new Chart(ctx, {
                type:    'polarArea',
                data:    {
                    labels:   this.labels,
                    datasets: [
                        {
                            // data: this.testDataGap,
                            data:            this.GapAnalysisData,
                            backgroundColor: this.universalBackgroundColors,
                        }],
                },
                options: {
                    plugins: {
                        legend: {
                            display: false,
                        }
                    },
                    scales:  {
                        r: {
                            ticks: {
                                stepSize: 1, // Set the interval between ticks
                                color:    'black' // Set the color of tick labels
                            },
                            min:   0, // Set the minimum value of the scale
                            max:   this.largestGap, // Set the maximum value of the scale
                            // max:         this.testDataLargestGap, // Set the maximum value of the scale
                            pointLabels: {
                                display: false,
                            },
                        },
                    },
                },
            });
        },
    },
}
</script>
